import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * A character's patrol, drawn over the map's walls.
 *
 * WHY WALLS AND NOT JUST THE PATH. The shape of a route on its own tells you
 * almost nothing — what you want to know is whether it hugs a wall, crosses a
 * doorway, or wanders somewhere it should not. That only reads against the
 * geometry it sits in.
 *
 * TILE UNITS COMING IN, WORLD UNITS ON THE WAYPOINTS. `solid` is indexed
 * `ty * widthTiles + tx`, while waypoints are world coordinates in foot space.
 * The conversion is one divide, and getting it backwards draws the route at
 * 1/16 scale in the corner — which reads as a bug in the route rather than in
 * the preview.
 *
 * Canvas rather than SVG: an 80x60 map is 4800 rectangles, which is a lot of
 * DOM for something redrawn every time you click a different character.
 */
function RoutePreview({ map, route }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !map) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return; // jsdom has no 2D context — draw nothing rather than throw

    const { widthTiles, heightTiles, tileSize } = map;

    // Fit the map into the box whatever its proportions, and centre it.
    const scale = Math.min(canvas.width / widthTiles, canvas.height / heightTiles);
    const ox = (canvas.width - widthTiles * scale) / 2;
    const oy = (canvas.height - heightTiles * scale) / 2;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#111827';
    ctx.fillRect(ox, oy, widthTiles * scale, heightTiles * scale);

    ctx.fillStyle = '#374151';
    (map.solid || []).forEach((index) => {
      const tx = index % widthTiles;
      const ty = Math.floor(index / widthTiles);
      // +1 on the size closes the hairline seams rounding leaves between
      // adjacent wall tiles at fractional scale.
      ctx.fillRect(ox + tx * scale, oy + ty * scale, scale + 1, scale + 1);
    });

    const points = route?.waypoints || [];
    if (points.length === 0) return;

    // World units -> tiles -> pixels.
    const px = (w) => ox + (w.x / tileSize) * scale;
    const py = (w) => oy + (w.y / tileSize) * scale;

    ctx.strokeStyle = '#F5D97A';
    ctx.lineWidth = 2;
    ctx.beginPath();
    points.forEach((w, i) => (i === 0 ? ctx.moveTo(px(w), py(w)) : ctx.lineTo(px(w), py(w))));
    // A loop returns to its first point. A pingpong retraces the line it came
    // along, so closing it would draw an edge the character never walks.
    if (route.mode === 'loop' && points.length > 2) ctx.closePath();
    ctx.stroke();

    points.forEach((w, i) => {
      ctx.beginPath();
      ctx.arc(px(w), py(w), 6, 0, Math.PI * 2);
      ctx.fillStyle = i === 0 ? '#34D399' : '#F5D97A';
      ctx.fill();

      ctx.fillStyle = '#111827';
      ctx.font = 'bold 9px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(i + 1), px(w), py(w));
    });
  }, [map, route]);

  if (!map) {
    return (
      <div className="flex h-40 items-center justify-center rounded border border-gray-300 text-xs text-gray-500 dark:border-gray-700">
        Loading map…
      </div>
    );
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={420}
        height={260}
        className="w-full rounded border border-gray-300 dark:border-gray-700"
      />
      <p className="mt-1 text-xs text-gray-500">
        Green is where they start.
        {' '}
        {route?.mode === 'loop' ? 'Loops back to the beginning.' : 'Paces back and forth.'}
      </p>
    </div>
  );
}

RoutePreview.propTypes = {
  map: PropTypes.shape({
    widthTiles: PropTypes.number,
    heightTiles: PropTypes.number,
    tileSize: PropTypes.number,
    solid: PropTypes.arrayOf(PropTypes.number),
  }),
  route: PropTypes.shape({
    mode: PropTypes.string,
    waypoints: PropTypes.arrayOf(PropTypes.shape({
      x: PropTypes.number, y: PropTypes.number,
    })),
  }),
};

RoutePreview.defaultProps = { map: null, route: null };

export default RoutePreview;
